import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ScanLine, Search, PackagePlus, CheckCircle2, AlertTriangle, Camera } from "lucide-react";
import { toast } from "sonner";
import { useAuditItems, useAudit } from "@/features/audits/api";
import { VerifyItemSheet } from "@/features/audits/VerifyItemSheet";
import { UntaggedAssetDialog } from "@/features/audits/UntaggedAssetDialog";
import { DiscrepancyBadge } from "@/features/audits/badges";
import type { AuditItemRow } from "@/features/audits/types";

export const Route = createFileRoute("/_authenticated/audits/$auditId/execute")({
  component: AuditExecute,
});

function AuditExecute() {
  const { auditId } = Route.useParams();
  const { data: audit } = useAudit(auditId);
  const { data: items = [] } = useAuditItems(auditId);
  const isLocked = audit?.status === "Completed" || audit?.status === "Signed Off";

  const [scanInput, setScanInput] = useState("");
  const [search, setSearch] = useState("");
  const [openItem, setOpenItem] = useState<AuditItemRow | null>(null);
  const [untaggedOpen, setUntaggedOpen] = useState(false);

  const filtered = useMemo(() => {
    if (!search.trim()) return items.filter((i) => !i.is_untagged_stub).slice(0, 100);
    const s = search.trim().toLowerCase();
    return items
      .filter((i) => !i.is_untagged_stub)
      .filter((i) =>
        (i.asset?.asset_code ?? "").toLowerCase().includes(s) ||
        (i.asset?.name ?? "").toLowerCase().includes(s) ||
        (i.asset?.serial_number ?? "").toLowerCase().includes(s),
      ).slice(0, 100);
  }, [items, search]);

  const handleScan = () => {
    const code = scanInput.trim();
    if (!code) return;
    // Try exact code match or extract code from URL/QR payload
    const codeOnly = code.includes("/") ? code.split("/").pop() ?? code : code;
    const found = items.find((i) => i.asset?.asset_code === code || i.asset?.asset_code === codeOnly);
    if (!found) {
      toast.error("Asset not in this audit scope");
      return;
    }
    setOpenItem(found);
    setScanInput("");
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold">Execute Audit</h2>
        <Button variant="outline" size="sm" onClick={() => setUntaggedOpen(true)} disabled={isLocked}>
          <PackagePlus className="mr-1 h-4 w-4" /> Untagged Asset
        </Button>
      </div>

      {isLocked && (
        <div className="flex items-start gap-2 rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>This audit is <strong>{audit?.status}</strong> and is locked. You cannot verify items or add untagged assets.</div>
        </div>
      )}

      <Tabs defaultValue="scan">
        <TabsList>
          <TabsTrigger value="scan"><ScanLine className="mr-1 h-4 w-4" />Scan / Enter Code</TabsTrigger>
          <TabsTrigger value="manual"><Search className="mr-1 h-4 w-4" />Manual Search</TabsTrigger>
        </TabsList>

        <TabsContent value="scan">
          <Card><CardContent className="space-y-3 p-4">
            <p className="text-sm text-muted-foreground">Use a USB/Bluetooth barcode scanner or paste a QR payload, then press Enter.</p>
            <div className="flex gap-2">
              <Input
                autoFocus
                placeholder="Asset code or QR URL"
                value={scanInput}
                disabled={isLocked}
                onChange={(e) => setScanInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleScan(); }}
              />
              <Button onClick={handleScan} disabled={isLocked}>Open</Button>
            </div>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="manual">
          <Card><CardContent className="space-y-2 p-4">
            <Input placeholder="Search by code, name or serial" value={search} onChange={(e) => setSearch(e.target.value)} />
            <div className="divide-y rounded border">
              {filtered.length === 0 && <div className="p-4 text-center text-sm text-muted-foreground">No matches.</div>}
              {filtered.map((it) => (
                <button
                  key={it.id}
                  type="button"
                  onClick={() => setOpenItem(it)}
                  disabled={isLocked}
                  className="flex w-full items-center justify-between gap-2 p-3 text-left hover:bg-accent/40 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="min-w-0">
                    <div className="font-mono text-xs">{it.asset?.asset_code}</div>
                    <div className="truncate text-sm">{it.asset?.name}</div>
                    <div className="text-xs text-muted-foreground">{it.expected_location?.name ?? "—"}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {it.photo_url && <Camera className="h-4 w-4 text-muted-foreground" />}
                    {it.verified_at ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : null}
                    <DiscrepancyBadge type={it.discrepancy_type} />
                  </div>
                </button>
              ))}
            </div>
          </CardContent></Card>
        </TabsContent>
      </Tabs>

      <VerifyItemSheet
        item={openItem}
        auditId={auditId}
        open={!!openItem}
        onOpenChange={(o) => { if (!o) setOpenItem(null); }}
      />
      <UntaggedAssetDialog auditId={auditId} open={untaggedOpen} onOpenChange={setUntaggedOpen} />
    </div>
  );
}
